import clsx from 'clsx';
import React, {
  HTMLAttributes,
  JSX,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icon } from '~/components/icon/Icon';
import { SvgIconNames } from '~/design-tokens/iconography/SvgIcons';

import styles from './Accordion.module.scss';

export interface AccordionItem {
  id?: string;
  titleIcon?: SvgIconNames;
  title: ReactNode;
  content?: ReactNode;
  rightAlignedChild?: ReactNode;
  renderIcon?: (expanded: boolean) => JSX.Element;
  hideIcon?: boolean;
  className?: string;
  props?: HTMLAttributes<HTMLDivElement>;
  value?: ReactNode;
  showHasValue?: boolean;
  expandedByDefault?: boolean;
  minHeight?: number;
  /** When true with `variant="secondary"`, the default leading `StarEmpty` is not rendered (compose icons in `title` instead). */
  hideDefaultTitleIcon?: boolean;
  footer?: ReactNode;
  autoHeight?: boolean;
}

export interface AccordionProps<T = AccordionItem>
  extends HTMLAttributes<HTMLDivElement> {
  withBackground?: boolean;
  variant?: 'primary' | 'secondary';
  items: T[];
  onItemClick?: (props: {
    item: T;
    event: MouseEvent<HTMLDivElement>;
    isExpanded: boolean;
  }) => void;
  collapseAccordionsOnClick?: boolean;
  className?: string;
  itemClassName?: string;
  defaultExpandedIds?: string[];
  duration?: number;
  disabledCollapse?: boolean;
}

export const Accordion = <T extends AccordionItem>({
  withBackground = true,
  items,
  onItemClick,
  collapseAccordionsOnClick = true,
  className,
  itemClassName,
  defaultExpandedIds,
  variant = 'primary',
  duration = 400,
  disabledCollapse = false,
  ...rest
}: AccordionProps<T>) => {
  const [expandedItemsIdx, setExpandedItemsIdx] = useState<Set<number>>(
    () => new Set()
  );
  const itemsContentHeights = useRef(new Map());

  const handleClick = ({
    event,
    accordion,
    idx,
  }: {
    event: MouseEvent<HTMLDivElement>;
    accordion: T;
    idx: number;
  }) => {
    if (!accordion.content) {
      return;
    }

    const newSet: Set<number> = collapseAccordionsOnClick
      ? new Set()
      : new Set(expandedItemsIdx);

    if (disabledCollapse) {
      newSet.add(idx);
      setExpandedItemsIdx(newSet);
    } else {
      expandedItemsIdx.has(idx) ? newSet.delete(idx) : newSet.add(idx);
      setExpandedItemsIdx(newSet);
    }

    onItemClick?.({
      event,
      item: accordion,
      isExpanded: !expandedItemsIdx.has(idx),
    });
  };

  useEffect(() => {
    const exp = items.reduce<number[]>((res, item, idx) => {
      if (item.expandedByDefault) {
        res.push(idx);
      }

      return res;
    }, []);

    setExpandedItemsIdx(new Set(exp));
  }, [items]);

  const itemsWithIds = items.map((item, idx) => ({
    ...item,
    id: item.id ?? (performance.now() + '').replace('.', '') + idx,
  }));

  return (
    <div
      data-accordion
      data-testid="accordion"
      className={clsx(styles.accordion, className, {
        [styles.withBackground]: withBackground,
        [styles.secondary]: variant === 'secondary',
      })}
      {...rest}
    >
      {itemsWithIds.map((accordionItem, idx) => (
        <React.Fragment key={idx}>
          <div
            data-accordion-item
            data-testid="accordion-item"
            {...accordionItem.props}
            className={clsx(
              styles.accordionItem,
              expandedItemsIdx.has(idx) && 'accordion-expanded',
              itemClassName,
              accordionItem.className,
              { [styles.hideIcon]: accordionItem.hideIcon }
            )}
          >
            <div
              data-accordion-title
              onClick={event =>
                handleClick({ event, accordion: accordionItem, idx })
              }
              aria-controls={accordionItem.id}
              role="button"
              data-testid="accordion-item-button"
              className={clsx(styles.titleContainer, {
                [styles.expandable]: !!accordionItem.content,
              })}
            >
              <span
                data-accordion-title-content
                className={styles.titleContent}
              >
                {(accordionItem.titleIcon ||
                  (variant === 'secondary' &&
                    !accordionItem.hideDefaultTitleIcon)) && (
                  <Icon
                    iconName={accordionItem.titleIcon ?? 'StarEmpty'}
                    fillColor="var(--icon-primary)"
                    size={18}
                  />
                )}
                {accordionItem.title}
              </span>
              <span className={styles.rightAlignedItems}>
                {accordionItem.rightAlignedChild}
                {accordionItem?.showHasValue && !!accordionItem.value && (
                  <>{accordionItem.value}</>
                )}
                {accordionItem?.renderIcon?.(expandedItemsIdx.has(idx)) ?? (
                  <Icon
                    data-accordion-icon
                    iconName="ChevronLightRight"
                    fillColor="var(--icon-primary)"
                    size={16}
                    className={clsx(styles.iconStyled, {
                      [styles.withDefaultIcon]: !accordionItem?.renderIcon,
                      [styles.expanded]: expandedItemsIdx.has(idx),
                    })}
                  />
                )}
              </span>
            </div>
            {!!accordionItem.content && (
              <div
                data-accordion-transition
                data-testid="transition-wrapper"
                className={clsx(styles.transitionWrapper, {
                  [styles.autoHeightTransition]: accordionItem.autoHeight,
                })}
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- CSS variable `--duration` is not in `React.CSSProperties`
                style={{ '--duration': `${duration}ms` } as React.CSSProperties}
                ref={node => {
                  if (!node) {
                    return;
                  }

                  const isExpanded = expandedItemsIdx.has(idx);

                  if (accordionItem.autoHeight) {
                    node.style.gridTemplateRows = isExpanded ? '1fr' : '0fr';
                  } else {
                    const height = itemsContentHeights.current.get(idx);
                    node.style.height = isExpanded ? `${height}px` : '0px';
                  }
                }}
              >
                <div
                  data-accordion-content
                  id={accordionItem.id}
                  className={styles.content}
                  ref={node => {
                    itemsContentHeights.current.set(
                      idx,
                      accordionItem.minHeight
                        ? accordionItem?.minHeight
                        : node?.clientHeight ?? 0
                    );
                  }}
                >
                  {accordionItem.content}
                </div>
              </div>
            )}
          </div>
          {accordionItem.footer}
        </React.Fragment>
      ))}
    </div>
  );
};
